if (!customElements.get('page-membership-benefits')) {
  customElements.define('page-membership-benefits', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs = [...this.querySelectorAll('[data-benefits-tab]')];
      this.modes = [...this.querySelectorAll('[data-benefits-mode]')].map(element => ({
        element,
        rail: element.querySelector('[data-benefits-rail]'),
        slides: [...element.querySelectorAll('[data-benefits-slide]')],
        navigation: element.querySelector('[data-benefits-navigation]'),
        dots: element.querySelector('[data-benefits-dots]'),
      }));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        }, { signal });
      });
      this.modes.forEach(mode => {
        mode.rail.addEventListener('scroll', () => this.updateNavigation(mode), { passive: true, signal });
      });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const block = event.target.closest('[data-block-id]');
        const blockId = event.detail?.blockId || block?.dataset.blockId;
        const index = this.tabs.findIndex(tab => tab.dataset.blockId === blockId);
        if (index >= 0) this.select(index);
        else if (blockId) this.modes.forEach(mode => {
          const position = this.visibleSlides(mode).findIndex(slide => slide.dataset.blockId === blockId);
          if (position >= 0) this.goTo(mode, position);
        });
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.modes.forEach(mode => this.updateNavigation(mode)));
      this.resizeObserver.observe(this);
      this.select(0, false);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }
    motion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
    visibleSlides(mode) {
      return mode.slides.filter(slide => !slide.hidden);
    }
    select(index, center = true) {
      const active = this.tabs[index]?.dataset.blockId;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      if (center && this.tabs[index]) {
        const tab = this.tabs[index];
        const menu = tab.parentElement;
        const rect = tab.getBoundingClientRect();
        const parent = menu.getBoundingClientRect();
        menu.scrollBy({ left: rect.left + rect.width / 2 - parent.left - parent.width / 2, behavior: this.motion() });
      }
      this.modes.forEach(mode => {
        if (this.tabs[index]) mode.element.setAttribute('aria-labelledby', this.tabs[index].id);
        mode.slides.forEach(slide => {
          slide.hidden = Boolean(slide.dataset.label.trim()) && slide.dataset.blockId !== active;
        });
        mode.rail.scrollTo({ left: 0, behavior: 'auto' });
        this.drawDots(mode);
        this.updateNavigation(mode);
      });
    }
    drawDots(mode) {
      const slides = this.visibleSlides(mode);
      mode.navigation.hidden = slides.length < 2;
      mode.dots.replaceChildren();
      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to image ${index + 1}`);
        dot.addEventListener('click', () => this.goTo(mode, index), { signal: this.controller.signal });
        mode.dots.append(dot);
      });
    }
    currentIndex(mode) {
      const slides = this.visibleSlides(mode);
      if (slides.length < 2) return 0;
      const step = slides[1].offsetLeft - slides[0].offsetLeft;
      return Math.max(0, Math.min(slides.length - 1, Math.round(mode.rail.scrollLeft / Math.max(1, step))));
    }
    updateNavigation(mode) {
      const index = this.currentIndex(mode);
      [...mode.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }
    goTo(mode, index) {
      const slides = this.visibleSlides(mode);
      if (!slides.length) return;
      const next = Math.max(0, Math.min(slides.length - 1, index));
      mode.rail.scrollTo({ left: slides[next].offsetLeft - slides[0].offsetLeft, behavior: this.motion() });
      if (this.motion() === 'auto') this.updateNavigation(mode);
    }
  });
}
